import { useState } from "react";
import { toast } from "sonner";
import {
  Bell,
  Building,
  Mail,
  Phone,
  Save,
  Truck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStore } from "@/context/StoreContext";
import { brandEmail, brandName } from "@/lib/brand";

export function AdminSettings() {
  const { storeSettings, updateStoreSettings } = useStore();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    storeName: storeSettings?.storeName || brandName || "ALAIRA HOUSE",
    supportEmail: storeSettings?.supportEmail || brandEmail || "support@alairahouse.com",
    supportPhone: storeSettings?.supportPhone || "+91 78419 28485",
    currency: "INR (₹)",
    freeShippingThreshold: storeSettings?.freeShippingThreshold ?? 999,
    standardShippingFee: storeSettings?.standardShippingFee ?? 99,
    whatsappAlerts: storeSettings?.whatsappAlerts ?? true,
    lowStockThreshold: storeSettings?.lowStockThreshold ?? 5,
  });

  const handleSave = (e) => {
    e.preventDefault();
    setLoading(true);
    updateStoreSettings(settings);
    setTimeout(() => {
      setLoading(false);
      toast.success("Store settings & shipping rules saved successfully! ⚙️✨");
    }, 400);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#64748b]">
          Configuration
        </p>
        <h1 className="mt-1 font-display text-3xl font-bold leading-tight text-[#0f172a]">
          Store Settings
        </h1>
        <p className="mt-1 text-sm text-[#64748b]">
          Manage brand details, shipping threshold rules, and automated notifications.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Brand & Contact Info */}
        <div className="rounded-2xl border border-[#e2e8f0]/60 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2.5 font-display text-lg font-bold text-[#0f172a]">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#0f172a]/10 text-[#0f172a]">
              <Building size={16} />
            </span>
            Brand & Contact Profile
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">
                Storefront Name
              </span>
              <input
                type="text"
                value={settings.storeName}
                onChange={(e) =>
                  setSettings({ ...settings, storeName: e.target.value })
                }
                className="mt-1.5 h-11 w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc]/30 px-3.5 text-sm font-semibold text-[#0f172a] outline-none focus:border-[#0f172a]"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">
                Display Currency
              </span>
              <input
                type="text"
                disabled
                value={settings.currency}
                className="mt-1.5 h-11 w-full rounded-xl border border-[#e2e8f0] bg-[#f1f5f9] px-3.5 text-sm font-semibold text-[#64748b] outline-none"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">
                Support Email
              </span>
              <div className="relative mt-1.5">
                <input
                  type="email"
                  value={settings.supportEmail}
                  onChange={(e) =>
                    setSettings({ ...settings, supportEmail: e.target.value })
                  }
                  className="h-11 w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc]/30 pl-10 pr-3.5 text-sm font-semibold text-[#0f172a] outline-none focus:border-[#0f172a]"
                />
                <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748b]" />
              </div>
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">
                Support Phone / WhatsApp
              </span>
              <div className="relative mt-1.5">
                <input
                  type="text"
                  value={settings.supportPhone}
                  onChange={(e) =>
                    setSettings({ ...settings, supportPhone: e.target.value })
                  }
                  className="h-11 w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc]/30 pl-10 pr-3.5 text-sm font-semibold text-[#0f172a] outline-none focus:border-[#0f172a]"
                />
                <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748b]" />
              </div>
            </label>
          </div>
        </div>

        {/* Shipping & Delivery Rules */}
        <div className="rounded-2xl border border-[#e2e8f0]/60 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2.5 font-display text-lg font-bold text-[#0f172a]">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#0f172a]/10 text-[#0f172a]">
              <Truck size={16} />
            </span>
            Shipping & Delivery Rules
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">
                Free Shipping Threshold (₹)
              </span>
              <input
                type="number"
                value={settings.freeShippingThreshold}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    freeShippingThreshold: Number(e.target.value),
                  })
                }
                className="mt-1.5 h-11 w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc]/30 px-3.5 text-sm font-semibold text-[#0f172a] outline-none focus:border-[#0f172a]"
              />
              <span className="mt-1 block text-[11px] text-[#64748b]">
                Orders equal or above ₹{settings.freeShippingThreshold} receive 100% Free Shipping.
              </span>
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">
                Standard Shipping Charge (₹)
              </span>
              <input
                type="number"
                value={settings.standardShippingFee}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    standardShippingFee: Number(e.target.value),
                  })
                }
                className="mt-1.5 h-11 w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc]/30 px-3.5 text-sm font-semibold text-[#0f172a] outline-none focus:border-[#0f172a]"
              />
              <span className="mt-1 block text-[11px] text-[#64748b]">
                Flat rate applied to orders below ₹{settings.freeShippingThreshold}.
              </span>
            </label>
          </div>
        </div>

        {/* Admin Alerts & Notifications */}
        <div className="rounded-2xl border border-[#e2e8f0]/60 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2.5 font-display text-lg font-bold text-[#0f172a]">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#0f172a]/10 text-[#0f172a]">
              <Bell size={16} />
            </span>
            Automation & Alert Preferences
          </div>
          <div className="space-y-3">
            <label className="flex items-center justify-between rounded-xl border border-[#e2e8f0]/60 bg-[#f8fafc]/30 p-3.5 text-sm font-semibold text-[#0f172a] cursor-pointer">
              <div>
                <span>Enable WhatsApp Customer Quick Action</span>
                <p className="text-xs text-[#64748b]">
                  Pre-fills confirmation messages directly in 3-Dot menu for pending orders.
                </p>
              </div>
              <input
                type="checkbox"
                checked={settings.whatsappAlerts}
                onChange={(e) =>
                  setSettings({ ...settings, whatsappAlerts: e.target.checked })
                }
                className="h-5 w-5 accent-[#0f172a]"
              />
            </label>

            <label className="flex items-center justify-between rounded-xl border border-[#e2e8f0]/60 bg-[#f8fafc]/30 p-3.5 text-sm font-semibold text-[#0f172a] cursor-pointer">
              <div>
                <span>Low Inventory Alert Threshold</span>
                <p className="text-xs text-[#64748b]">
                  Highlight products when stock drops below this count.
                </p>
              </div>
              <input
                type="number"
                value={settings.lowStockThreshold}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    lowStockThreshold: Number(e.target.value),
                  })
                }
                className="h-9 w-20 rounded-lg border border-[#e2e8f0] bg-white text-center text-sm font-bold text-[#0f172a] outline-none"
              />
            </label>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            type="submit"
            disabled={loading}
            className="h-11 px-8 text-xs font-bold uppercase tracking-widest shadow-lg shadow-[#0f172a]/20"
          >
            <Save size={15} /> {loading ? "Saving…" : "Save Configuration"}
          </Button>
        </div>
      </form>
    </div>
  );
}
